"use client";

import Link from "next/link";
import { Button } from "~/components/v3/themed/Button";
import { CommunityAvatar } from "~/components/v4/shadcn/CommunityAvatar";
import WhiteBreakpointStar from "./WhiteBreakpointStar";
import { DiscoveryCommunity } from "../actions";
import { CommunityWaitlistCard } from "./CommunityWaitlistCard";

type Props = DiscoveryCommunity;

const CommunityDiscoveryCards = ({ communities }: { communities: Props[] }) => {
    return (
        <div className="mb-4 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {communities?.map((community) => (
                <CommunityDiscoveryCard
                    key={community.id}
                    community={community}
                />
            ))}
        </div>
    );
};

const CommunityDiscoveryCard = ({ community }: { community: Props }) => {
    if (community.status === "WAITLIST") {
        return (
            <CommunityWaitlistCard key={community.id} community={community} />
        );
    }

    return (
        <div
            className="flex h-full w-full flex-col rounded-lg border border-v3-N-600 bg-v3-N-600 p-4 md:max-w-[384px]"
            key={community.id}
        >
            {/* Community header with avatar and name */}
            <Link
                href={`/beta/${community.slug}`}
                className="flex items-center gap-3"
            >
                <CommunityAvatar
                    community={{
                        id: community.id,
                        name: community.name,
                        image: community.image,
                    }}
                    width={40}
                    height={40}
                />
                <div>
                    <h3 className="text-[24px] font-[500] text-white">
                        {community.name}
                    </h3>
                </div>
            </Link>
            <div className="mt-2 flex items-center gap-2 text-sm text-v3-N-300">
                <span>
                    {community.followerCount.toLocaleString("en-US")} members
                </span>
                <span className="text-v3-N-600">
                    <WhiteBreakpointStar />
                </span>
                <span className="flex items-center gap-1">
                    {community.eventCount.toLocaleString("en-US")} events
                </span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm text-v3-N-300">
                <span>{community.description}</span>
            </div>
            {/* Spacer to push the badge and button to the bottom */}
            <div className="flex-grow" />
            {/* Category Badge - now with inline-flex to take only required width */}
            <div className="mb-4 space-x-2">
                {community.categories?.map((category) => {
                    return (
                        <span
                            key={`${category}-${community.id}`}
                            className="inline-flex items-center rounded-full bg-white/15 px-2 py-1 text-xs text-white"
                        >
                            {category}
                        </span>
                    );
                })}
            </div>
            {/* Action button */}
            <div>
                <Link href={`/${community.slug}`}>
                    <Button variant="secondary" className="w-full">
                        Go to community
                    </Button>
                </Link>
            </div>
        </div>
    );
};

export default CommunityDiscoveryCards;
